/**
 * `POST /internal/cron/finance-pnl-materialize` (#2410, epic #2405).
 *
 * Every 2 minutes: computes the 5-minute PnL usage pieces, seals UTC+8 days
 * past the 48 h grace from them into `finance_pnl_usage_daily_rollup` /
 * `_days`, and rechecks the newest sealed days. Derived tables only — never
 * `revenue_ledger`, allocations, balances, statements, or commissions.
 * A rechecked day whose pieces differ is rewritten and reported as drift.
 *
 * cron_run_history is written by control-loops' tick-level recordCronRun.
 */

export const FINANCE_PNL_MATERIALIZE_LOOKBACK_DAYS_DEFAULT = 400;
export const FINANCE_PNL_MATERIALIZE_LOOKBACK_DAYS_MAX = 800;
export const FINANCE_PNL_MATERIALIZE_RECHECK_DAYS_DEFAULT = 7;
export const FINANCE_PNL_MATERIALIZE_RECHECK_DAYS_MAX = 60;
export const FINANCE_PNL_MATERIALIZE_STATEMENT_TIMEOUT_MS = 5_000;
/** Route wall-clock budget. The control-loops fetch/handler timeouts sit above this. */
export const FINANCE_PNL_MATERIALIZE_WALL_MS = 20_000;
/**
 * Bounds for the caller's `wallMs`. The compiled wall is the ceiling: a
 * control-loops tick that has less left of its own minute shrinks the pass, no
 * caller may stretch it past the 20 s the route is designed around.
 */
export const FINANCE_PNL_MATERIALIZE_WALL_MIN_MS = 5_000;
export const FINANCE_PNL_MATERIALIZE_WALL_MAX_MS = FINANCE_PNL_MATERIALIZE_WALL_MS;
export const FINANCE_PNL_MATERIALIZE_DRIFT_LIMIT = 50;

export interface FinancePnlMaterializeCronRequest {
  readonly lookbackDays?: number;
  readonly recheckDays?: number;
  /**
   * Route wall budget for this call, from the caller's tick. Absent = the
   * compiled {@link FINANCE_PNL_MATERIALIZE_WALL_MS}; only a shorter value is
   * sent (a tick with less of its minute left), so a normal tick's body is
   * exactly what it was.
   */
  readonly wallMs?: number;
}

export interface FinancePnlMaterializeDrift {
  readonly date: string;
  readonly brand: string;
  readonly field: string;
  readonly stored: number;
  readonly recomputed: number;
}

export interface FinancePnlMaterializeCronResponse {
  readonly ok: boolean;
  readonly materializedDays: number;
  readonly recheckedDays: number;
  readonly drift: readonly FinancePnlMaterializeDrift[];
  readonly errors: readonly string[];
  /**
   * Sealed days in this lookback still missing a `_days` row after this
   * call. Additive — older clients ignore it. Zero when the window is
   * caught up (or when the remaining days failed rather than remaining).
   */
  readonly remaining_days?: number;
  /**
   * Shanghai days inside this lookback that are `shadow_cut` and were not
   * materialized or rechecked. Present only while `LEDGER_PNL_USAGE_READ`
   * is `1`. An off response does not have the key.
   */
  readonly skipped_sealed_days?: readonly string[];
  /** 5-minute PnL pieces computed or refreshed by this call. Additive. */
  readonly refreshed_pieces?: number;
}
